
import {
    Box,
    Typography,
    Button,
    CircularProgress,
} from '@mui/material';
import { Img_1, Img_2 } from "../../assets";
import { useAppContext } from '../../context/AppContext';
import Layout from '../../components/Layout';
import '../../index.css';
import { useState, useEffect } from 'react';
import ApiService from '../../services/api';
import { getDistributorInfoAndName } from '../../utils/tokenUtils';

const Welcome = () => {
    const { setCurrentView, userProfilesCache, getTranslation, getAllUserSessions, userId, token, sessionId } = useAppContext();
    const [sessionLength, setSessionLength] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    // Fetch user sessions to check length
    useEffect(() => {
        const fetchSessions = async () => {
            if (token && userId) {
                try {
                    setIsLoading(true);
                    const apiService = new ApiService(null, token);
                    const sessions = await getAllUserSessions(apiService, userId);
                    setSessionLength(sessions.length);
                } catch (error) {
                    console.error('Error fetching user sessions:', error);
                    setSessionLength(0);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };

        fetchSessions();
    }, [token, userId, getAllUserSessions]);

    const isSessionPending = !sessionId;

    const customerCards = [
        {
            title: getTranslation("Txt271"),
            description: getTranslation("Txt406"),
            image: Img_1,
            buttonText: getTranslation("Txt273"),
            action: () => setCurrentView('user-info'),
            disabled: isSessionPending,
            loading: isSessionPending,
        },
        {
            title: getTranslation("Txt274"),
            description: getTranslation("Txt275"),
            image: Img_2,
            buttonText: getTranslation("Txt276"),
            action: () => setCurrentView('MoreUserDetails'),
            disabled: isSessionPending || isLoading || sessionLength === 0,
            loading: isSessionPending || isLoading,
        }
    ];


    return (
        <Layout showBackButton={false}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { 
                        xs: "50px", 
                        md: "20px",
                        lg: "25px",
                        xl: "30px"
                    },
                    alignItems: "center",
                    justifyContent: { xs: "space-between", md: "flex-start" },
                    padding: { 
                        xs: "20px 0", 
                        md: "10px 15px",
                        lg: "15px 20px",
                        xl: "20px 25px"
                    },
                    maxWidth: { 
                        md: "1200px",
                        lg: "1400px",
                        xl: "1600px"
                    },
                    margin: "0 auto",
                    minHeight: "100vh",      // Ensure minimum viewport height
                    width: "100%",
                    paddingTop: { 
                        md: "40px",
                        lg: "50px",
                        xl: "60px"
                    },
                    paddingBottom: "40px",   // Add bottom padding for scroll space
                    boxSizing: "border-box",
                    // overflow: "auto"         // Allow scrolling when content exceeds viewport
                }}>
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { 
                        xs: "20px", 
                        md: "30px",
                        lg: "35px",
                        xl: "40px"
                    },
                    alignItems: { xs: "center", md: "center" },
                    justifyContent: { xs: "flex-start", md: "flex-start" },
                    width: "100%",
                    flex: 1,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: { 
                            xs: "30px", 
                            md: '25px',
                            lg: '30px',
                            xl: '35px'
                        },
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: { 
                                xs: "10px", 
                                md: '8px',
                                lg: '10px',
                                xl: '12px'
                            },
                        }}>
                            <Typography variant="h5" component="h1" sx={{
                                color: "var(--color-text-primary)",
                                textAlign: "center",
                                fontSize: { 
                                    xs: "14px",
                                    sm: "24px",
                                    md: "36px",
                                    lg: "28px",
                                    xl: "32px"
                                },
                                fontStyle: "normal",
                                fontWeight: { xs: 400, md: 500, lg: 600 },
                                lineHeight: { 
                                    xs: "1.2", 
                                    md: "1.3", 
                                    lg: "1.2", 
                                    xl: "1.1" 
                                },
                                letterSpacing: { 
                                    md: "0.3px", 
                                    lg: "0.5px", 
                                    xl: "0.8px" 
                                },
                                marginBottom: { 
                                    md: "6px", 
                                    lg: "10px", 
                                    xl: "15px" 
                                },
                                fontFamily: "HerbalifeFont"
                            }}>
                                {getTranslation("Txt1001")}
                            </Typography>
                            <Typography
                                sx={{
                                    color: "var(--color-text-secondary)",
                                    textAlign: "center",
                                    fontSize: { 
                                        xs: "14px",
                                        sm: "18px",
                                        md: "24px",
                                        lg: "18px",
                                        xl: "20px"
                                    },
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: { 
                                        xs: "1.2", 
                                        md: "1.4", 
                                        lg: "1.3", 
                                        xl: "1.2" 
                                    },
                                    letterSpacing: { 
                                        md: "0.2px", 
                                        lg: "0.3px", 
                                        xl: "0.5px" 
                                    },
                                    maxWidth: { 
                                        md: "480px",     // 1280x720 - compact width
                                        lg: "580px",     // 1366x657 - moderate width
                                        xl: "750px"      // 1920x950 - generous width
                                    },
                                    fontFamily: "HerbalifeFont"
                                }}
                            >
                                {getTranslation("Txt267")}
                            </Typography>
                        </Box>

                        <Box sx={{
                            width: '100%',
                            maxWidth: {
                                xs: '400px',
                                sm: '560px',
                                md: '100%',
                                lg: '550px',
                                xl: '680px',
                            },
                            padding: {
                                xs: '16px 12px',
                                sm: '20px 18px',
                                md: '24px 20px',
                                lg: '25px 30px',
                                xl: '30px 35px',
                            },
                            borderRadius: { 
                                xs: '12px', 
                                md: '14px', 
                                lg: '18px', 
                                xl: '22px' 
                            },
                            backgroundColor: 'var(--color-background)',
                            boxShadow: { 
                                xs: '0 2px 8px rgba(0, 0, 0, 0.1)', 
                                md: '0 3px 12px rgba(0, 0, 0, 0.08)', 
                                lg: '0 4px 16px rgba(0, 0, 0, 0.1)', 
                                xl: '0 6px 24px rgba(0, 0, 0, 0.12)' 
                            },
                            border: { md: '1px solid #f0f0f0' }
                        }}>
                            {userProfilesCache.data && (() => {
                                const { distributorName } = getDistributorInfoAndName(userProfilesCache.data);
                                return (
                                    <Typography sx={{
                                        color: 'var(--color-text-primary)',
                                        textAlign: 'center',
                                        fontSize: { 
                                            xs: '12px',
                                            sm: '16px',
                                            md: '22px',
                                            lg: '16px',
                                            xl: '18px'
                                        },
                                        fontWeight: { xs: 400, md: 500 },
                                        lineHeight: { xs: '1.4', md: '1.5' },
                                        marginBottom: { 
                                            xs: '12px', 
                                            md: '10px', 
                                            lg: '12px', 
                                            xl: '16px' 
                                        },
                                        letterSpacing: { 
                                            md: '0.2px', 
                                            lg: '0.3px', 
                                            xl: '0.4px' 
                                        },
                                        fontFamily: 'HerbalifeFont'
                                    }}>
                                        {getTranslation("Txt75").replace(/\[.*?\]/g, distributorName)}
                                    </Typography>
                                );
                            })()}
                            
                        {userProfilesCache.data && (() => {
                            const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);
                            return distributorInfo;
                        })() && (
                        
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: { xs: '4px', md: '20px' },
                                    fontSize: { 
                                        xs: '12px',
                                        sm: '15px',
                                        md: '18px',
                                        lg: '14px',
                                        xl: '16px'
                                    },
                                    color: 'var(--color-text-secondary)',
                                    padding: { 
                                        md: '8px 0', 
                                        lg: '10px 0', 
                                        xl: '12px 0' 
                                    }
                                }}>
                                                                 {(() => {
                                        const { distributorInfo } = getDistributorInfoAndName(userProfilesCache.data);

                                        return distributorInfo ? (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    color: 'inherit',
                                                    fontFamily: 'HerbalifeFont',
                                                    textAlign: 'center',
                                                    fontWeight: { md: 500 },
                                                    letterSpacing: { 
                                                        md: '0.2px', 
                                                        lg: '0.3px', 
                                                        xl: '0.4px' 
                                                    }
                                                }}>
                                                    <strong>{getTranslation("Txt825")+":"}</strong> {distributorInfo.primary_email}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: 'inherit',
                                                    color: 'inherit',
                                                    fontFamily: 'HerbalifeFont',
                                                    textAlign: 'center',
                                                    fontWeight: { md: 500 },
                                                    letterSpacing: { 
                                                        md: '0.2px', 
                                                        lg: '0.3px', 
                                                        xl: '0.4px' 
                                                    }
                                                }}>
                                                    <strong>{getTranslation("Txt77").split(':')[0]+":"}</strong> {distributorInfo.phone_number}
                                                </Typography>
                                            </Box>
                                        ) : null;
                                    })()}
                                </Box>
                        )}
                        </Box>

                        {/* Mobile Design (xs, sm) */}
                        <Box sx={{
                            width: '100%',
                            maxWidth: { xs: '400px', sm: '560px' },
                            padding: '0',
                            display: { xs: 'block', md: 'none' }
                        }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr',
                                gap: { xs: '12px', sm: '16px' },
                                width: '100%'
                            }}>
                                {customerCards.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            padding: { xs: '16px', sm: '20px' },
                                            borderRadius: { xs: '12px', sm: '16px' },
                                            // backgroundColor: '#f8f8f8',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                            gap: { xs: '16px', sm: '20px' },
                                            justifyContent: 'space-between',
                                            height: '100%'
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            gap: { xs: '16px', sm: '20px' }
                                        }}>
                                            <Box
                                                sx={{
                                                    width: { xs: '50px', sm: '64px' },
                                                    height: { xs: '50px', sm: '64px' },
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                    border: '2px solid #e0e0e0'
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={customerCards[index].image}
                                                    alt={customerCards[index].title}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '6px'
                                            }}>
                                                <Typography
                                                    sx={{
                                                        color: 'var(--color-text-primary)',
                                                        fontSize: { xs: '16px', sm: '18px' },
                                                        fontWeight: 600,
                                                        lineHeight: '1.2'
                                                    }}
                                                >
                                                    {customerCards[index].title}
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color: 'var(--color-text-secondary)',
                                                        fontSize: { xs: '14px', sm: '16px' },
                                                        fontWeight: 400,
                                                        lineHeight: '1.3'
                                                    }}
                                                >
                                                    {customerCards[index].description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            width: '100%'
                                        }}>
                                            <Button
                                                variant="contained"
                                                onClick={customerCards[index].action}
                                                disabled={customerCards[index].disabled}
                                                sx={{
                                                    padding: { xs: '8px 16px', sm: '10px 20px' },
                                                    borderRadius: '6px',
                                                    background: customerCards[index].disabled ? '#ccc' : '#000',
                                                    fontSize: { xs: '14px', sm: '16px' },
                                                    color: '#FFF',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    minWidth: { xs: '100px', sm: '120px' },
                                                    height: { xs: '36px', sm: '42px' },
                                                    '&:hover': {
                                                        background: customerCards[index].disabled ? '#ccc' : '#333',
                                                    },
                                                }}
                                            >
                                                {customerCards[index].loading ? (
                                                    <CircularProgress size={20} color="inherit" />
                                                ) : (
                                                    customerCards[index].buttonText
                                                )}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Desktop Design (md and up) */}
                        <Box sx={{
                            display: { xs: 'none', md: 'flex' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            padding: {
                                md: '20px 16px',
                                lg: '20px 0',
                                xl: '25px 0',
                            },
                            boxSizing: 'border-box',
                        }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    md: 'repeat(2, minmax(0, 1fr))',
                                    lg: 'repeat(2, 1fr)',
                                },
                                gap: {
                                    md: '24px',
                                    lg: '30px',
                                    xl: '35px',
                                },
                                justifyContent: 'center',
                                alignItems: 'stretch',
                                maxWidth: {
                                    md: '100%',
                                    lg: '1000px',
                                    xl: '1300px',
                                },
                                width: '100%',
                                boxSizing: 'border-box',
                            }}>
                                {customerCards.map((_, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            justifyContent: 'space-between',
                                            padding: {
                                                md: '28px 20px',
                                                lg: '30px',
                                                xl: '35px',
                                            },
                                            borderRadius: {
                                                md: '20px',
                                                lg: '24px',
                                                xl: '28px',
                                            },
                                            border: '1px solid #e8e8e8',
                                            backgroundColor: '#FFF',
                                            boxShadow: {
                                                md: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                                lg: '0 3px 12px rgba(0, 0, 0, 0.05)',
                                                xl: '0 4px 16px rgba(0, 0, 0, 0.06)',
                                            },
                                            gap: {
                                                md: '20px',
                                                lg: '25px',
                                                xl: '30px',
                                            },
                                            height: 'auto',
                                            width: '100%',
                                            minWidth: 0,
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: {
                                                    md: '120px',
                                                    lg: '100px',
                                                    xl: '110px',
                                                },
                                                height: {
                                                    md: '120px',
                                                    lg: '100px',
                                                    xl: '110px',
                                                },
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                border: '3px solid #f0f0f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#f8f8f8',
                                                boxShadow: {
                                                    md: '0 3px 12px rgba(0, 0, 0, 0.08)',
                                                    lg: '0 4px 15px rgba(0, 0, 0, 0.1)',
                                                    xl: '0 5px 18px rgba(0, 0, 0, 0.12)',
                                                },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={customerCards[index].image}
                                                alt={customerCards[index].title}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    borderRadius: '50%'
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px',
                                                width: '100%',
                                                minWidth: 0,
                                            }}
                                        >
                                        <Typography
                                            sx={{
                                                color: 'var(--color-text-primary)',
                                                textAlign: 'center',
                                                fontFamily: 'HerbalifeFont',
                                                fontSize: {
                                                    md: '24px',
                                                    lg: '20px',
                                                    xl: '22px',
                                                },
                                                fontStyle: 'normal',
                                                fontWeight: 600,
                                                lineHeight: { md: '1.3', lg: '1.2' },
                                                margin: 0,
                                                letterSpacing: {
                                                    md: '0.3px',
                                                    lg: '0.5px',
                                                    xl: '0.7px',
                                                },
                                            }}
                                        >
                                            {customerCards[index].title}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: 'var(--color-text-secondary)',
                                                textAlign: 'center',
                                                fontFamily: 'HerbalifeFont',
                                                fontSize: {
                                                    md: '20px',
                                                    lg: '15px',
                                                    xl: '16px',
                                                },
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: {
                                                    md: '1.5',
                                                    lg: '1.4',
                                                    xl: '1.3',
                                                },
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                minWidth: 0,
                                                maxWidth: {
                                                    md: '100%',
                                                    lg: '300px',
                                                    xl: '350px',
                                                },
                                                letterSpacing: {
                                                    md: '0.2px',
                                                    lg: '0.3px',
                                                    xl: '0.4px',
                                                },
                                            }}
                                        >
                                            {customerCards[index].description}
                                        </Typography>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            onClick={customerCards[index].action}
                                            disabled={customerCards[index].disabled}
                                            sx={{
                                                width: {
                                                    md: '180px',
                                                    lg: '150px',
                                                    xl: '160px',
                                                },
                                                height: {
                                                    md: '48px',
                                                    lg: '42px',
                                                    xl: '45px',
                                                },
                                                borderRadius: {
                                                    md: '6px',
                                                    lg: '8px',
                                                    xl: '10px',
                                                },
                                                background: customerCards[index].disabled ? '#ccc' : '#000',
                                                fontSize: {
                                                    md: '18px',
                                                    lg: '15px',
                                                    xl: '16px',
                                                },
                                                color: '#FFF',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontFamily: 'HerbalifeFont',
                                                letterSpacing: {
                                                    md: '0.3px',
                                                    lg: '0.4px',
                                                    xl: '0.6px',
                                                },
                                                boxShadow: customerCards[index].disabled ? 'none' : {
                                                    md: '0 3px 12px rgba(0, 0, 0, 0.15)',
                                                    lg: '0 4px 15px rgba(0, 0, 0, 0.2)',
                                                    xl: '0 5px 18px rgba(0, 0, 0, 0.25)',
                                                },
                                                '&:hover': {
                                                    background: customerCards[index].disabled ? '#ccc' : '#333',
                                                    transform: customerCards[index].disabled ? 'none' : 'scale(1.05)',
                                                    boxShadow: customerCards[index].disabled ? 'none' : {
                                                        md: '0 5px 18px rgba(0, 0, 0, 0.25)',
                                                        lg: '0 6px 20px rgba(0, 0, 0, 0.3)',
                                                        xl: '0 8px 25px rgba(0, 0, 0, 0.35)',
                                                    },
                                                },
                                                '&:active': {
                                                    transform: customerCards[index].disabled ? 'none' : 'scale(0.98)',
                                                },
                                            }}
                                        >
                                            {customerCards[index].loading ? (
                                                <CircularProgress size={22} color="inherit" />
                                            ) : (
                                                customerCards[index].buttonText
                                            )}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Layout>
    );
};

export default Welcome;
